import type { TournamentMatch } from "./api";

/**
 * Regla de ALIASES del gate #136 (6º re-gate), compartida por los productores
 * de opciones de semana/jornada (AdminTournamentStandingsPage,
 * PlayerRankingPage, PlayerTournamentGamesPage, AdminTournamentSchedulePage):
 * un mismo número de ronda puede convivir con round_labels crudos DISTINTOS
 * ("Semana 4" y "Jornada 4", datos legados). El display debe ser estable y
 * fail-safe:
 * - etapa confiable (la MISMA para todos los aliases) → etiqueta canónica;
 * - un solo raw → ese raw exacto;
 * - varios raws sin etapa común → display combinado determinista
 *   ("Jornada 4 · Semana 4", ordenado) — NUNCA el primer alias silencioso.
 */

/** Orden determinista de aliases (es-MX, numérico). */
export function sortRawLabels(raws: Iterable<string>): string[] {
  return [...raws].sort((a, b) => a.localeCompare(b, "es", { numeric: true }));
}

/** Separador visible del display combinado de aliases. */
export const RAW_LABEL_ALIAS_SEPARATOR = " · ";

/** "Jornada 4 · Semana 4": todos los aliases, ordenados, visibles. */
export function combinedRawLabelDisplay(raws: string[]): string {
  return sortRawLabels(raws).join(RAW_LABEL_ALIAS_SEPARATOR);
}

/**
 * Raw labels REALES almacenados en los matches, agrupados por número de
 * ronda y ordenados de forma determinista (el resultado NO depende del orden
 * de las filas). Espejo del criterio numérico de los productores de semanas.
 */
export function matchRawLabelsBySort(
  matches: Array<Pick<TournamentMatch, "round_label">>,
): Map<number, string[]> {
  const collected = new Map<number, Set<string>>();
  for (const match of matches) {
    const raw = (match.round_label ?? "").trim();
    if (!raw) continue;
    const found = raw.match(/(\d+)/);
    const sort = found ? Number(found[1]) : Number.MAX_SAFE_INTEGER - 1;
    const bucket = collected.get(sort) ?? new Set<string>();
    bucket.add(raw);
    collected.set(sort, bucket);
  }
  const out = new Map<number, string[]>();
  for (const [sort, labels] of collected) {
    out.set(sort, sortRawLabels(labels));
  }
  return out;
}

/**
 * Etapa canónica de un conjunto de aliases: SOLO cuando TODOS los aliases
 * resuelven a la MISMA etapa (así la canónica los cubre a todos). Si alguno
 * no tiene etapa — o difieren entre divisiones — se devuelve undefined y el
 * consumidor cae al display crudo combinado (fail-safe, nunca una etapa que
 * solo aplica a parte del bucket).
 */
export function stageForRawLabels(
  raws: string[],
  stageByLabel: Record<string, string>,
): string | undefined {
  if (raws.length === 0) return undefined;
  const stages = raws.map((raw) => stageByLabel[raw.trim()]);
  if (stages.some((stage) => !stage)) return undefined;
  return new Set(stages).size === 1 ? stages[0] : undefined;
}
